import { useEffect, useState } from 'react';
import { api, ApiError, type PipelinesPayload } from '../api';
import { useStore } from '../state/store';
import { Badge, Button, Card, CardBody, EmptyState } from '../design/primitives';

/**
 * Delivery pipelines.
 *
 * CI is where agents get the most authority and the least supervision, so the
 * coverage gap is stated plainly at the top rather than buried. A pipeline
 * without the drift gate is reported as unknown, not as safe.
 */
export function DevOps() {
  const { focusTrace } = useStore();
  const [data, setData] = useState<PipelinesPayload>();
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    api.pipelines()
      .then(setData)
      .catch(caught => setError(caught.message));
  }, []);

  const run = async (pipelineId: string) => {
    setBusy(pipelineId);
    setError(undefined);
    try {
      const outcome = await api.runPipeline(pipelineId);
      focusTrace(outcome.traceId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That pipeline could not be run.');
    } finally {
      setBusy(undefined);
    }
  };

  if (error && !data) return <EmptyState>Could not load pipelines: {error}</EmptyState>;
  if (!data) return null;

  return (
    <div className="mb-page">
      <Card>
        <CardBody>
          <p className="mb-lede">
            Meridian's pipelines contain steps that an AI agent wrote — migrations, deploy scripts, evidence uploads.
            The runners are enrolled in the fleet like any laptop, so those steps are gated the same way.
          </p>

          <div className="mb-stat-row">
            <div className="mb-stat">
              <div className="mb-stat__value">{data.coverage.total}</div>
              <div className="mb-stat__label">Pipelines</div>
            </div>
            <div className="mb-stat mb-stat--ok">
              <div className="mb-stat__value">{data.coverage.gated}</div>
              <div className="mb-stat__label">With a drift gate</div>
            </div>
            <div className={`mb-stat${data.coverage.gaps.length > 0 ? ' mb-stat--warn' : ''}`}>
              <div className="mb-stat__value">{data.coverage.gaps.length}</div>
              <div className="mb-stat__label">Coverage gaps</div>
            </div>
          </div>

          {data.coverage.gaps.length > 0 && (
            <p className="mb-note">
              No drift gate on {data.coverage.gaps.join(', ')}. Agent-authored changes there are recorded but not
              checked at merge time — reported as a known gap rather than assumed safe.
            </p>
          )}
        </CardBody>
      </Card>

      {error && (
        <p className="mb-op__error" role="alert">
          {error}
        </p>
      )}

      <section>
        <h2 className="mb-section-title">Pipelines</h2>
        <div className="mb-pipeline-list">
          {data.pipelines.map((pipeline: any) => (
            <Card key={pipeline.id}>
              <CardBody>
                <div className="mb-pipeline__head">
                  <div className="mb-grow">
                    <h3 className="mb-section-title" style={{ marginBottom: 0 }}>
                      {pipeline.title}
                    </h3>
                    <div className="mb-pipeline__meta">
                      <code className="mb-mono">{pipeline.repo}</code> · {pipeline.workflow} · runs on{' '}
                      {pipeline.hostId}
                    </div>
                  </div>
                  <Badge tone={pipeline.protectedByCi ? 'allow' : 'approval'}>
                    {pipeline.protectedByCi ? 'drift gate active' : 'no drift gate'}
                  </Badge>
                </div>

                <p className="mb-section-sub" style={{ marginTop: 'var(--s-2)' }}>
                  {pipeline.description}
                </p>

                <ol className="mb-pipeline__steps">
                  {pipeline.steps.map((step: any) => (
                    <li key={step.name}>
                      <span className="mb-grow">{step.name}</span>
                      {step.agentAuthored && (
                        <Badge tone="info" glyph={false}>
                          authored by an agent
                        </Badge>
                      )}
                    </li>
                  ))}
                </ol>

                <Button variant="primary" size="sm" disabled={busy === pipeline.id} onClick={() => run(pipeline.id)}>
                  {busy === pipeline.id ? 'Running…' : 'Run pipeline'}
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
